import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import authService from '../../../../services/auth.service';
import { FiMail, FiLock, FiUser, FiBriefcase, FiPhone, FiCreditCard, FiAlertCircle, FiCheckCircle, FiArrowRight, FiArrowLeft, FiCheck, FiZap } from 'react-icons/fi';
import './Login.css';
import '../../../../styles/Pricing.css';

// Definición de planes disponibles
const PLANES = [
    {
        id: 'trial',
        nombre: 'Prueba Gratis',
        descripcion: '30 días de acceso completo',
        basePrice: 0,
        icon: '🆓',
        disponible: true,
        features: ['Acceso a todas las herramientas', 'Sin tarjeta de crédito', '30 días de prueba']
    },
    {
        id: 'cotizaplus',
        nombre: 'CotizaPlus',
        descripcion: 'Cotizaciones inteligentes',
        basePrice: 239880,  // Anual
        icon: '✦',
        disponible: true,
        features: ['Cotizaciones ilimitadas', 'Módulo de Autoatención', 'PDFs y WhatsApp']
    },
    {
        id: 'crm',
        nombre: 'CRM Plus',
        descripcion: 'Gestión de relaciones comerciales',
        basePrice: null,
        icon: '★',
        disponible: false,
        features: ['Embudo de Ventas', 'Seguimiento de Leads', 'Recordatorios']
    },
    {
        id: 'inventario',
        nombre: 'Inventario Plus',
        descripcion: 'Control de stock y proveedores',
        basePrice: null,
        icon: '❖',
        disponible: false,
        features: ['Multi-bodega', 'Alertas de stock', 'Órdenes de compra']
    },
    {
        id: 'suite',
        nombre: 'Suite IdevPlus',
        descripcion: 'Todos los módulos integrados',
        basePrice: null,
        icon: '🚀',
        disponible: false,
        features: ['Todos los módulos', 'Descuento especial', 'Integración nativa']
    }
];

const formatCLP = (price) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(price);

const TOTAL_STEPS = 4;

const Register = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [componentError, setComponentError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('trial');

    const { register, handleSubmit, trigger, watch, getValues, setError, formState: { errors } } = useForm({
        defaultValues: {
            pais: 'Chile',
            moneda_base: 'CLP'
        },
        mode: 'onChange'
    });

    const password = watch('password');
    const paisSeleccionado = watch('pais');
    const formValues = watch();

    const planActual = PLANES.find(p => p.id === selectedPlan);
    const isPaidPlan = planActual && planActual.basePrice > 0;
    const planPrice = planActual?.basePrice || 0;
    const monthlyEquivalent = planPrice > 0 ? Math.round(planPrice / 12) : 0;
    const planPriceLabel = planActual?.basePrice === 0 ? 'Gratis' : planActual?.basePrice ? `${formatCLP(monthlyEquivalent)}/mes` : 'Próximamente';

    const onSubmit = async (data) => {
        setComponentError('');
        setSuccess('');
        setLoading(true);

        if (data.rut) {
            data.rut = data.rut.replace(/\./g, '');
        }

        // Incluir plan seleccionado en los datos
        data.plan_seleccionado = selectedPlan;

        try {
            const response = await authService.registerCompany(data);
            if (isPaidPlan && response.pago_id) {
                setSuccess('Cuenta creada. Redirigiendo al proceso de pago...');
                setTimeout(() => { navigate(`/payment/checkout/${response.pago_id}`); }, 1500);
            } else {
                setSuccess('Cuenta creada exitosamente. Redirigiendo...');
                setTimeout(() => {
                    navigate('/registro-exitoso', {
                        state: {
                            isPaid: false,
                            planName: planActual?.nombre || 'Prueba Gratis',
                            email: data.email,
                            empresaNombre: data.empresa_nombre,
                        }
                    });
                }, 1500);
            }
        } catch (err) {
            console.error(err);
            let errorMsg = 'Error al registrar empresa.';
            const resp = err.response?.data;

            if (resp) {
                if (resp.detail) errorMsg = resp.detail;
                else if (resp.email) errorMsg = `Email: ${resp.email[0]}`;
                else if (resp.rut) errorMsg = `RUT/Doc: ${resp.rut[0]}`;
                else if (typeof resp === 'string') {
                    if (resp.includes('Data too long')) errorMsg = 'Error interno: Datos demasiado largos.';
                    else if (resp.trim().startsWith('<')) errorMsg = 'Error interno del servidor (500).';
                    else errorMsg = resp;
                } else {
                    errorMsg = Object.entries(resp).map(([key, val]) => `${key}: ${val}`).join(', ');
                }
            }
            setComponentError(errorMsg);
            setStep(4);
        } finally {
            setLoading(false);
        }
    };

    const nextStep = async (fieldsToValidate) => {
        if (fieldsToValidate && fieldsToValidate.length > 0) {
            const isValid = await trigger(fieldsToValidate);
            if (!isValid) return;
        }

        setLoading(true);
        setComponentError('');
        try {
            if (step === 1 && fieldsToValidate?.includes('email')) {
                const email = getValues('email');
                const resp = await authService.checkAvailability({ email });
                if (!resp.email_available) {
                    setError('email', { type: 'manual', message: 'Este email ya está en uso' });
                    setLoading(false);
                    return;
                }
            }
            if (step === 2 && fieldsToValidate?.includes('rut')) {
                const rut = getValues('rut');
                if (rut) {
                    const cleanRut = rut.replace(/\./g, '');
                    const resp = await authService.checkAvailability({ rut: cleanRut });
                    if (!resp.rut_available) {
                        setError('rut', { type: 'manual', message: 'Este RUT/Identificador ya está registrado' });
                        setLoading(false);
                        return;
                    }
                }
            }
        } catch (err) {
            console.error('Error al validar disponibilidad:', err);
        }
        setLoading(false);
        setStep(step + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const prevStep = () => {
        setStep(step - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const stepLabels = ['Personal', 'Negocio', 'Plan', 'Resumen'];

    const getStepSubtitle = () => {
        switch (step) {
            case 1: return 'Datos personales';
            case 2: return 'Datos del negocio';
            case 3: return 'Elige tu plan';
            case 4: return 'Confirma tu registro';
            default: return '';
        }
    };

    const getProgressWidth = () => {
        if (step === 1) return '0%';
        return `${((step - 1) / (TOTAL_STEPS - 1)) * 100}%`;
    };

    return (
        <div className="login-page-bg">
            <div className="visual-placeholder" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'hidden', zIndex: 0 }}>
                <div className="floating-circle circle-1"></div>
                <div className="floating-circle circle-2" style={{ bottom: '10%', left: '10%' }}></div>
            </div>

            <div className="login-container" style={{ zIndex: 1, position: 'relative', maxWidth: '480px' }}>
                <div className="login-card" style={{ width: '100%', padding: '30px' }}>
                    <div className="login-header" style={{ marginBottom: '20px' }}>
                        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem', fontWeight: 500, marginBottom: '15px' }}>
                            ← Volver al inicio
                        </Link>
                        <h1 style={{ fontSize: '1.8rem', color: '#1a1a2e', fontWeight: '800' }}>Crear Cuenta</h1>
                        <p style={{ color: '#64748b', fontSize: '0.95rem', marginTop: '5px' }}>
                            Paso {step} de {TOTAL_STEPS} — {getStepSubtitle()}
                        </p>
                    </div>

                    {/* Progress Indicator - 4 steps */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '30px', position: 'relative' }}>
                        <div style={{ position: 'absolute', top: '50%', left: '10%', right: '10%', height: '2px', backgroundColor: '#e2e8f0', zIndex: 0, transform: 'translateY(-50%)' }}>
                            <div style={{ height: '100%', backgroundColor: '#ec4899', width: getProgressWidth(), transition: 'width 0.4s ease' }}></div>
                        </div>

                        {stepLabels.map((label, idx) => {
                            const stepNum = idx + 1;
                            const isActive = step >= stepNum;
                            const isCompleted = step > stepNum;
                            return (
                                <React.Fragment key={label}>
                                    {idx > 0 && <div style={{ flex: 1 }}></div>}
                                    <div style={{ zIndex: 1, display: 'flex', alignItems: 'center', gap: '6px', background: 'white', padding: '0 6px' }}>
                                        <div style={{
                                            width: '28px', height: '28px', borderRadius: '50%',
                                            background: isActive ? '#ec4899' : '#f1f5f9',
                                            color: isActive ? 'white' : '#94a3b8',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontWeight: 'bold', fontSize: '0.8rem', transition: 'all 0.3s'
                                        }}>
                                            {isCompleted ? <FiCheck size={14} /> : stepNum}
                                        </div>
                                        <span style={{ fontWeight: isActive ? '600' : '400', color: isActive ? '#ec4899' : '#64748b', fontSize: '0.75rem' }}>{label}</span>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>

                    {componentError && (
                        <div className="alert-custom alert-danger">
                            <FiAlertCircle size={20} />
                            <span>{componentError}</span>
                        </div>
                    )}

                    {success && (
                        <div className="alert-custom alert-success">
                            <FiCheckCircle size={20} />
                            <span>{success}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)} className="login-form">

                        {/* ================= STEP 1: ADMINISTRADOR ================= */}
                        {step === 1 && (
                            <div className="step-content animation-fade-in">
                                <h4 style={{ fontSize: '1rem', marginBottom: '20px', color: '#1e293b', fontWeight: 'bold' }}>Tus Datos de Acceso</h4>

                                <div className="grid-1-to-2" style={{ gap: '15px' }}>
                                    <div className="form-group-custom">
                                        <label className="form-label-custom"><FiUser size={16} /> Nombre *</label>
                                        <input {...register('first_name', { required: 'Obligatorio' })} className={`form-control-custom ${errors.first_name ? 'input-error' : ''}`} placeholder="Juan" />
                                        {errors.first_name && <span className="form-error">{errors.first_name.message}</span>}
                                    </div>
                                    <div className="form-group-custom">
                                        <label className="form-label-custom"><FiUser size={16} /> Apellido *</label>
                                        <input {...register('last_name', { required: 'Obligatorio' })} className={`form-control-custom ${errors.last_name ? 'input-error' : ''}`} placeholder="Pérez" />
                                        {errors.last_name && <span className="form-error">{errors.last_name.message}</span>}
                                    </div>
                                </div>

                                <div className="form-group-custom">
                                    <label className="form-label-custom"><FiMail size={16} /> Email de Administrador *</label>
                                    <input type="email" {...register('email', { required: 'Email es obligatorio', pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Email inválido" } })} className={`form-control-custom ${errors.email ? 'input-error' : ''}`} placeholder="admin@empresa.com" />
                                    {errors.email && <span className="form-error">{errors.email.message}</span>}
                                </div>

                                <div className="grid-1-to-2" style={{ gap: '15px' }}>
                                    <div className="form-group-custom">
                                        <label className="form-label-custom"><FiLock size={16} /> Contraseña *</label>
                                        <input type="password" {...register('password', { required: 'Contraseña obligatoria', minLength: { value: 6, message: "Mínimo 6 caracteres" }, validate: (val) => { if (!/[A-Z]/.test(val)) return 'Debe tener una mayúscula'; if (!/[a-z]/.test(val)) return 'Debe tener una minúscula'; if (!/[0-9]/.test(val)) return 'Debe tener un número'; return true; } })} className={`form-control-custom ${errors.password ? 'input-error' : ''}`} placeholder="••••••••" />
                                        {errors.password && <span className="form-error">{errors.password.message}</span>}
                                    </div>
                                    <div className="form-group-custom">
                                        <label className="form-label-custom"><FiLock size={16} /> Repetir Contraseña *</label>
                                        <input type="password" {...register('confirm_password', { required: 'Confirme su contraseña', validate: value => value === password || "Las contraseñas no coinciden" })} className={`form-control-custom ${errors.confirm_password ? 'input-error' : ''}`} placeholder="••••••••" />
                                        {errors.confirm_password && <span className="form-error">{errors.confirm_password.message}</span>}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                                    <button type="button" className="btn-custom btn-primary" onClick={() => nextStep(['first_name', 'last_name', 'email', 'password', 'confirm_password'])} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        Siguiente Paso <FiArrowRight />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ================= STEP 2: EMPRESA ================= */}
                        {step === 2 && (
                            <div className="step-content animation-fade-in">
                                <h4 style={{ fontSize: '1rem', marginBottom: '20px', color: '#1e293b', fontWeight: 'bold' }}>Datos de la Empresa</h4>

                                <div className="form-group-custom">
                                    <label className="form-label-custom"><FiBriefcase size={16} /> Nombre o Razón Social *</label>
                                    <input {...register('empresa_nombre', { required: 'Nombre de empresa obligatorio' })} className={`form-control-custom ${errors.empresa_nombre ? 'input-error' : ''}`} placeholder="Ej: Mi Pyme SpA" />
                                    {errors.empresa_nombre && <span className="form-error">{errors.empresa_nombre.message}</span>}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                                    <div className="form-group-custom">
                                        <label className="form-label-custom"><FiBriefcase size={16} /> País *</label>
                                        <select {...register('pais', { required: 'País obligatorio' })} className="form-control-custom">
                                            <option value="Chile">Chile</option>
                                            <option value="México">México</option>
                                            <option value="Perú">Perú</option>
                                            <option value="Otro">Otro País</option>
                                        </select>
                                    </div>
                                    <div className="form-group-custom">
                                        <label className="form-label-custom"><FiBriefcase size={16} /> Moneda Base *</label>
                                        <select {...register('moneda_base', { required: 'Moneda obligatoria' })} className="form-control-custom">
                                            <option value="CLP">CLP (Peso Chileno)</option>
                                            <option value="MXN">MXN (Peso Mexicano)</option>
                                            <option value="PEN">PEN (Sol Peruano)</option>
                                            <option value="USD">USD (Dólar)</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid-1-to-2" style={{ gap: '15px', marginBottom: '15px' }}>
                                    <div className="form-group-custom">
                                        <label className="form-label-custom">
                                            <FiCreditCard size={16} /> {paisSeleccionado === 'Chile' ? 'RUT Empresa *' : paisSeleccionado === 'Perú' ? 'RUC Empresa *' : paisSeleccionado === 'México' ? 'RFC Empresa *' : 'Identificador Fiscal *'}
                                        </label>
                                        <input
                                            {...register('rut', {
                                                required: 'Identificador obligatorio',
                                                validate: (value) => {
                                                    if (!value) return true;
                                                    if (paisSeleccionado === 'Chile') {
                                                        const clean = value.replace(/\./g, '').replace(/-/g, '').toUpperCase();
                                                        if (clean.length < 2) return 'RUT inválido';
                                                        const cuerpo = clean.slice(0, -1);
                                                        const dv = clean.slice(-1);
                                                        if (!/^\d+$/.test(cuerpo)) return 'Formato inválido';
                                                        let suma = 0, multiplo = 2;
                                                        for (let i = cuerpo.length - 1; i >= 0; i--) {
                                                            suma += parseInt(cuerpo.charAt(i)) * multiplo;
                                                            multiplo = multiplo < 7 ? multiplo + 1 : 2;
                                                        }
                                                        const dvCalculado = (11 - (suma % 11)) === 11 ? '0' : (11 - (suma % 11)) === 10 ? 'K' : (11 - (suma % 11)).toString();
                                                        return dv === dvCalculado || 'RUT inválido';
                                                    }
                                                    if (value.length < 5) return 'Muy corto';
                                                    return true;
                                                }
                                            })}
                                            className={`form-control-custom ${errors.rut ? 'input-error' : ''}`}
                                            placeholder={paisSeleccionado === 'Chile' ? "76.123.456-7" : "12345678"}
                                            onChange={(e) => {
                                                if (paisSeleccionado === 'Chile') {
                                                    let raw = e.target.value.replace(/[^0-9kK]/g, '').toUpperCase();
                                                    if (raw.length > 1) {
                                                        e.target.value = `${raw.slice(0, -1)}-${raw.slice(-1)}`;
                                                    } else e.target.value = raw;
                                                } else e.target.value = e.target.value.replace(/\s/g, '').toUpperCase();
                                            }}
                                        />
                                        {errors.rut && <span className="form-error">{errors.rut.message}</span>}
                                    </div>
                                    <div className="form-group-custom">
                                        <label className="form-label-custom"><FiPhone size={16} /> Teléfono {paisSeleccionado === 'Chile' ? '🇨🇱' : '🌐'}</label>
                                        <input {...register('telefono', { pattern: { value: /^[0-9+ \-]+$/, message: 'Formato inválido' } })} className={`form-control-custom ${errors.telefono ? 'input-error' : ''}`} placeholder="+56 9 1234 5678" />
                                        {errors.telefono && <span className="form-error">{errors.telefono.message}</span>}
                                    </div>
                                </div>

                                <div className="form-group-custom">
                                    <label className="form-label-custom"><FiBriefcase size={16} /> Dirección de la Empresa</label>
                                    <input {...register('direccion')} className="form-control-custom" placeholder="Ej: Av. Providencia 123" />
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                                    <button type="button" className="btn-custom" onClick={prevStep} style={{ background: '#f1f5f9', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FiArrowLeft /> Volver
                                    </button>
                                    <button type="button" className="btn-custom btn-primary" onClick={() => nextStep(['empresa_nombre', 'pais', 'moneda_base', 'rut', 'telefono', 'direccion'])} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        Continuar <FiArrowRight />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ================= STEP 3: SELECCIÓN DE PLAN ================= */}
                        {step === 3 && (
                            <div className="step-content animation-fade-in">
                                <h4 style={{ fontSize: '1rem', marginBottom: '5px', color: '#1e293b', fontWeight: 'bold' }}>Elige tu Plan</h4>
                                <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '15px' }}>Selecciona el plan que mejor se adapte a tu negocio</p>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                                    {PLANES.map((plan) => {
                                        const isSelected = selectedPlan === plan.id;
                                        const isDisabled = !plan.disponible;
                                        return (
                                            <div
                                                key={plan.id}
                                                onClick={() => !isDisabled && setSelectedPlan(plan.id)}
                                                style={{
                                                    padding: '14px 16px',
                                                    borderRadius: '12px',
                                                    border: isSelected ? '2px solid transparent' : '1px solid #e2e8f0',
                                                    background: isSelected ? 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #4f46e5, #ec4899) border-box' : isDisabled ? '#f8fafc' : 'white',
                                                    borderWidth: isSelected ? '2px' : '1px',
                                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                    opacity: isDisabled ? 0.5 : 1,
                                                    transition: 'all 0.25s ease',
                                                    position: 'relative',
                                                    boxShadow: isSelected ? '0 4px 15px rgba(79, 70, 229, 0.15), 0 0 20px rgba(236, 72, 153, 0.08)' : '0 1px 3px rgba(0,0,0,0.04)'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        {/* Radio button */}
                                                        <div style={{
                                                            width: '22px', height: '22px', borderRadius: '50%',
                                                            border: isSelected ? 'none' : '2px solid #cbd5e1',
                                                            background: isSelected ? 'linear-gradient(135deg, #4f46e5, #ec4899)' : 'white',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            flexShrink: 0, transition: 'all 0.25s',
                                                            boxShadow: isSelected ? '0 2px 8px rgba(79, 70, 229, 0.3)' : 'none'
                                                        }}>
                                                            {isSelected && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'white' }}></div>}
                                                        </div>
                                                        <div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <span style={{ fontSize: '1.1rem' }}>{plan.icon}</span>
                                                                <strong style={{ color: '#1e293b', fontSize: '0.95rem' }}>{plan.nombre}</strong>
                                                            </div>
                                                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{plan.descripcion}</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                        {(() => {
                                                            const label = plan.basePrice === 0 ? 'Gratis' : plan.id === 'cotizaplus' ? '$19.990/mes' : plan.basePrice ? `${formatCLP(Math.round(plan.basePrice / 12))}/mes` : 'Próximamente';
                                                            return (
                                                                <>
                                                                    <span style={{ fontWeight: 'bold', fontSize: '0.95rem', background: isDisabled ? 'none' : 'linear-gradient(135deg, #4f46e5, #ec4899)', WebkitBackgroundClip: isDisabled ? 'unset' : 'text', WebkitTextFillColor: isDisabled ? '#94a3b8' : 'transparent', backgroundClip: isDisabled ? 'unset' : 'text' }}>
                                                                        {label}
                                                                    </span>
                                                                    {plan.basePrice > 0 && (
                                                                        <p style={{ fontSize: '0.7rem', color: '#94a3b8', margin: '2px 0 0 0', fontWeight: 500 }}>
                                                                            Facturado anualmente ({formatCLP(plan.basePrice)})
                                                                        </p>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>

                                                {/* Features expandidas cuando está seleccionado */}
                                                {isSelected && plan.disponible && (
                                                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(79, 70, 229, 0.1)' }}>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                            {plan.features.map((f, i) => (
                                                                <span key={i} style={{ fontSize: '0.73rem', background: 'linear-gradient(135deg, rgba(79,70,229,0.08), rgba(236,72,153,0.08))', color: '#4f46e5', padding: '4px 10px', borderRadius: '20px', border: '1px solid rgba(79,70,229,0.15)', fontWeight: 500 }}>
                                                                    ✓ {f}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {isDisabled && (
                                                    <span style={{ position: 'absolute', top: '8px', right: '10px', fontSize: '0.65rem', background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)', color: '#64748b', padding: '2px 8px', borderRadius: '10px', fontWeight: 500 }}>
                                                        En desarrollo
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                                    <button type="button" className="btn-custom" onClick={prevStep} style={{ background: '#f1f5f9', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FiArrowLeft /> Volver
                                    </button>
                                    <button type="button" className="btn-custom btn-primary" onClick={() => nextStep([])} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        Ver Resumen <FiArrowRight />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ================= STEP 4: RESUMEN Y CONFIRMACIÓN ================= */}
                        {step === 4 && (
                            <div className="step-content animation-fade-in">
                                <h4 style={{ fontSize: '1rem', marginBottom: '20px', color: '#1e293b', fontWeight: 'bold' }}>Resumen de tu Registro</h4>

                                {/* Plan seleccionado */}
                                <div style={{ padding: '16px', borderRadius: '12px', border: '2px solid transparent', background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #4f46e5, #ec4899) border-box', marginBottom: '15px', boxShadow: '0 4px 15px rgba(79, 70, 229, 0.1)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span style={{ fontSize: '1.3rem' }}>{planActual?.icon}</span>
                                            <div>
                                                <strong style={{ color: '#1e293b', fontSize: '0.95rem' }}>{planActual?.nombre}</strong>
                                                <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0 }}>{planActual?.descripcion}</p>
                                                {isPaidPlan && <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>Facturado anualmente</span>}
                                            </div>
                                        </div>
                                        <span style={{ fontWeight: 'bold', fontSize: '1rem', background: 'linear-gradient(135deg, #4f46e5, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{planPriceLabel}</span>
                                    </div>
                                </div>

                                {/* Datos del usuario */}
                                <div style={{ background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', border: '1px solid rgba(79,70,229,0.1)', borderRadius: '12px', padding: '15px', marginBottom: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
                                    <h5 style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#334155', borderBottom: '1px solid rgba(79,70,229,0.08)', paddingBottom: '8px', marginBottom: '12px' }}>Tus Datos</h5>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.85rem' }}>
                                        <div>
                                            <p style={{ color: '#64748b', margin: '0 0 3px 0', fontSize: '0.8rem' }}>Administrador</p>
                                            <strong style={{ color: '#1e293b' }}>{formValues.first_name} {formValues.last_name}</strong>
                                            <p style={{ margin: '3px 0 0 0', color: '#475569', fontSize: '0.8rem' }}>{formValues.email}</p>
                                        </div>
                                        <div>
                                            <p style={{ color: '#64748b', margin: '0 0 3px 0', fontSize: '0.8rem' }}>Empresa</p>
                                            <strong style={{ color: '#1e293b' }}>{formValues.empresa_nombre}</strong>
                                            <p style={{ margin: '3px 0 0 0', color: '#475569', fontSize: '0.8rem' }}>{formValues.rut || 'Sin Identificador'}</p>
                                            <p style={{ margin: '2px 0 0 0', color: '#475569', fontSize: '0.75rem' }}>{formValues.pais} • {formValues.moneda_base}</p>
                                        </div>
                                    </div>
                                </div>

                                {isPaidPlan && (
                                    <div style={{ borderRadius: '12px', border: '2px solid transparent', background: 'linear-gradient(rgba(255,251,235,0.95), rgba(254,249,195,0.95)) padding-box, linear-gradient(135deg, #f59e0b, #d97706) border-box', padding: '12px 14px', marginBottom: '15px', fontSize: '0.82rem', color: '#78350f', boxShadow: '0 2px 10px rgba(245,158,11,0.15), 0 0 15px rgba(245,158,11,0.05)', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                        <FiZap size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: '1px' }} />
                                        <div><strong>Pago requerido:</strong> Después de registrarte, serás redirigido al proceso de pago. Tu cuenta se activará una vez confirmado el pago.</div>
                                    </div>
                                )}

                                <p style={{ fontSize: '0.8rem', color: '#64748b', textAlign: 'center', marginBottom: '15px' }}>
                                    Al hacer clic en "{isPaidPlan ? 'Registrar y Pagar' : 'Finalizar Registro'}", aceptas nuestros Términos de Servicio y Política de Privacidad.
                                </p>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px' }}>
                                    <button type="button" className="btn-custom" onClick={prevStep} style={{ background: '#f1f5f9', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FiArrowLeft /> Volver
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-custom"
                                        disabled={loading}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '8px',
                                            background: isPaidPlan ? 'linear-gradient(135deg, #4f46e5, #ec4899)' : 'linear-gradient(135deg, #10b981, #059669)',
                                            color: 'white', padding: '12px 20px', border: 'none', borderRadius: '8px', fontWeight: 'bold',
                                            boxShadow: isPaidPlan ? '0 4px 15px rgba(79,70,229,0.3)' : '0 4px 15px rgba(16,185,129,0.3)'
                                        }}
                                    >
                                        {loading ? 'Procesando...' : isPaidPlan ? 'Registrar y Pagar' : 'Finalizar Registro'} <FiCheckCircle />
                                    </button>
                                </div>
                            </div>
                        )}

                    </form>

                    <div className="login-footer" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #f1f5f9' }}>
                        <p>¿Ya tienes una cuenta? <Link to="/login" style={{ color: 'var(--primary-color)', fontWeight: 'bold' }}>Inicia Sesión</Link></p>
                    </div>
                </div>
            </div>

            <style>
                {`
                .animation-fade-in {
                    animation: fadeIn 0.4s ease forwards;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .input-error {
                    border-color: #ef4444 !important;
                    background-color: #fef2f2 !important;
                }
                `}
            </style>
        </div>
    );
};

export default Register;
